<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink } from 'vue-router'

const { t, tm, locale } = useI18n()
const isDark = ref(false)
const isMobileMenuOpen = ref(false)

const languages = [
  { code: 'en', label: 'English', dir: 'ltr' },
  { code: 'ar', label: 'العربية', dir: 'rtl' },
  { code: 'id', label: 'Bahasa Indonesia', dir: 'ltr' }
]

const currentLang = computed(() => {
    return languages.find(l => l.code === locale.value) || languages[0]
})

const changeLanguage = (code: string) => {
    const lang = languages.find(l => l.code === code)
    if (lang) {
        locale.value = code
        localStorage.setItem('locale', code)
        document.documentElement.dir = lang.dir
        document.documentElement.lang = code
    }
}

const toggleTheme = () => {
  isDark.value = !isDark.value
  if (isDark.value) {
    document.documentElement.classList.add('dark')
    localStorage.setItem('theme', 'dark')
  } else {
    document.documentElement.classList.remove('dark')
    localStorage.setItem('theme', 'light')
  }
}

const toggleMobileMenu = () => {
    isMobileMenuOpen.value = !isMobileMenuOpen.value
    document.body.style.overflow = isMobileMenuOpen.value ? 'hidden' : ''
}

onMounted(() => {
  const savedTheme = localStorage.getItem('theme')
  if (savedTheme === 'dark' || (!savedTheme && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
    isDark.value = true
    document.documentElement.classList.add('dark')
  }
  const savedLang = localStorage.getItem('locale')
  if (savedLang) {
      const langConfig = languages.find(l => l.code === savedLang)
      if (langConfig) {
          document.documentElement.dir = langConfig.dir
          document.documentElement.lang = savedLang
      }
  }
})
</script>

<template>
  <div class="min-h-screen bg-[#FDFBF7] dark:bg-bg-primary text-bg-primary dark:text-text-primary font-sans transition-colors duration-300">
    <!-- Navigation -->
    <nav class="sticky top-0 z-50 bg-[#FDFBF7]/90 dark:bg-bg-primary/90 backdrop-blur-md border-b border-gray-200/50 dark:border-border transition-colors duration-300">
      <div class="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        <RouterLink to="/" class="flex items-center">
          <img v-if="isDark" src="/images/logo_net_white.png" alt="Nomadian Logo" class="h-32 w-auto" />
          <img v-else src="/images/logo_net_black.png" alt="Nomadian Logo" class="h-32 w-auto" />
        </RouterLink>
        
        <!-- Desktop Nav -->
        <div class="hidden md:flex items-center gap-6">
            <div class="flex items-center space-x-8 rtl:space-x-reverse text-sm font-medium text-gray-800 dark:text-gray-200">
                <RouterLink to="/" class="hover:text-blue-600 dark:hover:text-white transition-colors">{{ t('footer.links.home') }}</RouterLink>
                <RouterLink to="/philosophy" class="hover:text-blue-600 dark:hover:text-white transition-colors">{{ t('nav.philosophy') }}</RouterLink>
                <RouterLink to="/about" class="hover:text-blue-600 dark:hover:text-white transition-colors">{{ t('nav.about') }}</RouterLink>
            </div>
            
            <div class="flex items-center gap-3 border-l pl-6 rtl:pl-0 rtl:border-l-0 rtl:border-r rtl:pr-6 border-gray-300 dark:border-gray-700">
                <div class="relative group">
                    <button class="flex items-center gap-1 text-sm font-medium hover:text-blue-600 dark:hover:text-white">
                        <span>{{ currentLang.code.toUpperCase() }}</span>
                        <span class="text-[10px]">▼</span>
                    </button>
                    <div class="absolute top-full right-0 rtl:right-auto rtl:left-0 mt-2 w-32 bg-white dark:bg-bg-card rounded-lg shadow-lg border border-gray-100 dark:border-border py-1 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all transform origin-top-right rtl:origin-top-left z-50">
                        <button 
                            v-for="lang in languages" 
                            :key="lang.code"
                            @click="changeLanguage(lang.code)"
                            class="w-full text-left rtl:text-right px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-bg-elevated transition-colors"
                            :class="{'font-bold text-blue-600': locale === lang.code}"
                        >
                            {{ lang.label }}
                        </button>
                    </div>
                </div>
                <button @click="toggleTheme" class="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition-colors">
                    <span v-if="isDark">☀️</span>
                    <span v-else>🌙</span>
                </button>
            </div>
        </div>

        <!-- Mobile Toggle -->
        <div class="flex items-center gap-4 md:hidden">
            <button @click="toggleTheme" class="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition-colors">
                <span v-if="isDark">☀️</span>
                <span v-else>🌙</span>
            </button>
            <button @click="toggleMobileMenu" class="p-2 -mr-2">
                <div class="w-6 h-5 flex flex-col justify-between">
                    <span class="w-full h-0.5 bg-current transform transition-transform" :class="{'rotate-45 translate-y-2': isMobileMenuOpen}"></span>
                    <span class="w-full h-0.5 bg-current opacity-100 transition-opacity" :class="{'opacity-0': isMobileMenuOpen}"></span>
                    <span class="w-full h-0.5 bg-current transform transition-transform" :class="{'-rotate-45 -translate-y-2.5': isMobileMenuOpen}"></span>
                </div>
            </button>
        </div>
      </div>
    </nav>

    <!-- Mobile Menu Overlay -->
    <div v-if="isMobileMenuOpen" class="fixed inset-0 z-40 bg-[#FDFBF7] dark:bg-bg-primary pt-24 px-6 md:hidden">
        <div class="flex flex-col space-y-6 text-xl font-medium">
             <RouterLink to="/" @click="toggleMobileMenu" class="border-b border-gray-200 dark:border-gray-800 pb-4">{{ t('footer.links.home') }}</RouterLink>
             <RouterLink to="/philosophy" @click="toggleMobileMenu" class="border-b border-gray-200 dark:border-gray-800 pb-4">{{ t('nav.philosophy') }}</RouterLink>
             <RouterLink to="/about" @click="toggleMobileMenu" class="border-b border-gray-200 dark:border-gray-800 pb-4">{{ t('nav.about') }}</RouterLink>
             
             <div class="pt-4 flex gap-2 overflow-x-auto pb-2">
                 <button 
                    v-for="lang in languages"
                    :key="lang.code"
                    @click="changeLanguage(lang.code)"
                    class="px-4 py-2 rounded-full border text-sm whitespace-nowrap"
                    :class="locale === lang.code ? 'border-black dark:border-white bg-black dark:bg-white text-white dark:text-black' : 'border-gray-300 dark:border-gray-700'"
                 >
                    {{ lang.label }}
                 </button>
             </div>
        </div>
    </div>

    <main>
      <!-- About Hero Section -->
      <section class="py-16 md:py-24 px-6">
        <div class="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-start">
          <div>
            <h1 class="text-4xl md:text-5xl font-serif font-medium mb-8">{{ t('pages.about.title') }}</h1>
            
            <div class="space-y-6 text-gray-700 dark:text-gray-300 leading-relaxed">
              <p>{{ t('pages.about.story1') }}</p>
              <p class="font-medium text-bg-primary dark:text-white">{{ t('pages.about.story2') }}</p>
              <p>{{ t('pages.about.story3') }}</p>
              <p>{{ t('pages.about.story4') }}</p>
            </div>
          </div>
          
          <div class="relative">
            <div class="aspect-[4/3] rounded-2xl overflow-hidden border border-gray-200 dark:border-border">
              <img src="/images/mecca.jpg" alt="Makkah" class="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <!-- Vision & Mission Section -->
      <section class="bg-[#6B7B6A] text-white py-16 md:py-24 px-6">
        <div class="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20">
          <div>
            <p class="text-sm uppercase tracking-wide text-white/70 mb-6">{{ t('pages.about.visionLabel') }}</p>
            <h2 class="text-3xl md:text-4xl font-serif leading-tight">
              {{ t('pages.about.visionText') }}
            </h2>
          </div>
          
          <div>
            <p class="text-sm uppercase tracking-wide text-white/70 mb-6">{{ t('pages.about.missionLabel') }}</p>
            <p class="text-xl leading-relaxed mb-8">
              {{ t('pages.about.missionText') }}
            </p>
            
            <!-- Hotel Image -->
            <div class="aspect-[4/3] rounded-xl overflow-hidden">
              <img src="/images/hotel_about.jpg" alt="Premium Hotel Property" class="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <!-- Team Section -->
      <section class="bg-bg-primary dark:bg-black py-16 md:py-24 px-6">
        <div class="max-w-7xl mx-auto">
          <h2 class="text-3xl md:text-4xl font-serif text-white mb-12 text-center">{{ t('pages.about.teamTitle') }}</h2>
          
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <div 
              v-for="(member, index) in (tm('pages.about.team') as any[])" 
              :key="index" 
              class="bg-bg-card/50 border border-border/50 rounded-2xl p-6 hover:border-border transition-all duration-300 group text-center"
            >
              <!-- Photo -->
              <div class="aspect-square rounded-xl overflow-hidden bg-gray-200 dark:bg-bg-elevated mb-5">
                <img v-if="member.photo" :src="member.photo" :alt="member.name" class="w-full h-full object-cover object-top" :style="index === 3 ? 'transform: scale(1.4); transform-origin: top center;' : ''" />
                <div v-else class="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-600">
                  <svg class="w-20 h-20" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                  </svg>
                </div>
              </div>
              
              <!-- Info -->
              <h3 class="text-white font-semibold text-lg">{{ member.name }}</h3>
              <p class="text-gray-400 text-sm mt-1">{{ member.role }}</p>
              
              <!-- Social links -->
              <div class="flex items-center justify-center gap-3 mt-4">
                <a 
                  v-if="member.twitter"
                  :href="member.twitter" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  class="text-gray-500 hover:text-white transition-colors"
                >
                  <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>
                <a
                  v-if="member.linkedin"
                  :href="member.linkedin"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-gray-500 hover:text-white transition-colors"
                >
                  <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
                <!-- Show placeholder icons when no social links -->
                <template v-if="!member.twitter && !member.linkedin">
                  <span class="text-gray-600">
                    <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </span>
                  <span class="text-gray-600">
                    <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  </span>
                </template>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Values Section -->
      <section class="py-16 md:py-24 px-6">
        <div class="max-w-7xl mx-auto">
          <h2 class="text-3xl md:text-4xl font-serif mb-16 text-center">{{ t('pages.about.valuesTitle') }}</h2>
          
          <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div v-for="(value, index) in (tm('pages.about.values') as any[])" :key="index" class="p-8 bg-white dark:bg-bg-card rounded-2xl border border-gray-100 dark:border-border hover:shadow-lg transition-shadow duration-300">
              <h3 class="font-bold text-lg mb-3">{{ value.title }}</h3>
              <p class="text-gray-600 dark:text-gray-400 text-sm mb-4 leading-relaxed">{{ value.desc }}</p>
              <ul v-if="value.points" class="space-y-2">
                <li v-for="(point, pIndex) in value.points" :key="pIndex" class="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <span class="text-[#6B7B6A] dark:text-[#8B9B8A] mt-0.5 flex-shrink-0">✓</span>
                  <span>{{ point }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </main>

    <!-- Footer -->
    <footer class="bg-bg-primary text-white py-20 px-6 dark:bg-black dark:border-t dark:border-border">
      <div class="max-w-7xl mx-auto">
        <div class="flex flex-col md:flex-row justify-between items-start mb-16 gap-12">
             <div class="font-bold text-2xl">Nomadian Elite</div>
             <div class="grid grid-cols-2 gap-16">
                 <div>
                     <h4 class="text-sm font-semibold uppercase tracking-wider text-gray-300 mb-4">{{ t('footer.navTitle') }}</h4>
                     <div class="flex flex-col gap-3 text-sm text-gray-400">
                         <RouterLink to="/" class="hover:text-white transition-colors">{{ t('footer.links.home') }}</RouterLink>
                         <RouterLink to="/philosophy" class="hover:text-white transition-colors">{{ t('nav.philosophy') }}</RouterLink>
                         <RouterLink to="/about" class="hover:text-white transition-colors">{{ t('footer.links.about') }}</RouterLink>
                     </div>
                 </div>
                 <div>
                     <h4 class="text-sm font-semibold uppercase tracking-wider text-gray-300 mb-4">{{ t('footer.legalTitle') }}</h4>
                     <div class="flex flex-col gap-3 text-sm text-gray-400">
                         <a href="#" class="hover:text-white transition-colors">{{ t('footer.links.privacy') }}</a>
                         <a href="#" class="hover:text-white transition-colors">{{ t('footer.links.terms') }}</a>
                     </div>
                 </div>
             </div>
        </div>
        
        <div class="border-t border-gray-800 pt-12 text-xs text-gray-500 leading-relaxed text-justify">
            <p class="mb-4">
               {{ t('footer.disclaimer1') }}
            </p>
            <p>
               {{ t('footer.disclaimer2') }}
            </p>
             <div class="mt-8 flex justify-between items-center">
                <span>{{ t('footer.copyright') }}</span>
                <div class="flex gap-4">
                    <span>Twitter</span>
                    <span>LinkedIn</span>
                </div>
             </div>
        </div>
      </div>
    </footer>
  </div>
</template>
